import {
  Badge,
  Box,
  Container,
  Flex,
  Heading,
  HStack,
  IconButton,
  Input,
  Stat,
  StatLabel,
  StatNumber,
  Text,
  Tooltip,
  useColorModeValue,
  useToast,
  VStack,
} from "@chakra-ui/react"
import { useQuery } from "@tanstack/react-query"
import {
  createFileRoute,
  getRouteApi,
  useNavigate,
} from "@tanstack/react-router"
import { useCallback, useEffect, useMemo, useState } from "react"
import { FiDownload, FiPlus, FiSearch } from "react-icons/fi"
import Select from "@/components/Common/AppSelect"
import { previewPostedDocumentPdf } from "../../../../api/documentPdf"
import {
  type ProductionOrderListDTO,
  type ProductionOrderListResponseDTO,
  ProductionOrderService,
} from "../../../../client"
import ProductionOrderList from "../../../../components/WMS/ProductionOrders/ProductionOrderList"
import {
  getProductionOrderStatusLabel,
  PRODUCTION_ORDER_STATUS_OPTIONS,
} from "../../../../components/WMS/ProductionOrders/statusUtils"
import PrintLanguageSelect from "../../../../components/WMS/shared/PrintLanguageSelect"

const PAGE_SIZE_OPTIONS = [25, 50, 100, 200] as const
const STATUS_STORAGE_KEY = "wms-production-orders-status-filter"
const VALID_STATUSES = ["draft", "active", "closed", "cancelled"] as const

export const Route = createFileRoute(
  "/_layout/WMS/production-orders/production-orders-list",
)({
  validateSearch: (
    search: Record<string, unknown>,
  ): {
    page: number
    page_size: number
    status?: string
  } => {
    const rawPage =
      typeof search.page === "number"
        ? search.page
        : typeof search.page === "string"
          ? Number.parseInt(search.page, 10) || 1
          : 1
    const rawPageSize =
      typeof search.page_size === "number"
        ? search.page_size
        : typeof search.page_size === "string"
          ? Number.parseInt(search.page_size, 10) || 25
          : 25
    const rawStatus =
      typeof search.status === "string" ? search.status : undefined

    const page_size = PAGE_SIZE_OPTIONS.includes(
      rawPageSize as (typeof PAGE_SIZE_OPTIONS)[number],
    )
      ? rawPageSize
      : 25

    const status =
      rawStatus && VALID_STATUSES.includes(rawStatus as any)
        ? rawStatus
        : undefined

    return {
      page: rawPage,
      page_size,
      ...(status ? { status } : {}),
    }
  },
  component: ProductionOrderListPage,
})

const productionOrdersRouteApi = getRouteApi(
  "/_layout/WMS/production-orders/production-orders-list",
)

function ProductionOrderListPage() {
  const navigate = useNavigate()
  const search = productionOrdersRouteApi.useSearch() as {
    page: number
    page_size: number
    status?: string
  }
  const toast = useToast()
  const [query, setQuery] = useState("")
  const [status, setStatus] = useState<string>(() => {
    if (search.status !== undefined) {
      return search.status
    }
    if (typeof window !== "undefined") {
      const stored = localStorage.getItem(STATUS_STORAGE_KEY)
      if (
        stored !== null &&
        (stored === "" || VALID_STATUSES.includes(stored as any))
      ) {
        return stored
      }
    }
    return ""
  })
  const [selected, setSelected] = useState<ProductionOrderListDTO[]>([])
  const panelBg = useColorModeValue("white", "gray.800")
  const panelBorder = useColorModeValue("gray.200", "whiteAlpha.200")
  const statBg = useColorModeValue("white", "gray.800")
  const statBorder = useColorModeValue("gray.200", "whiteAlpha.200")
  const statLabelColor = useColorModeValue("gray.600", "gray.300")
  const statValueColor = useColorModeValue("gray.900", "white")
  const page = search.page > 0 ? search.page : 1
  const pageSize = search.page_size

  useEffect(() => {
    if (search.status !== undefined) {
      setStatus(search.status)
      if (typeof window !== "undefined") {
        localStorage.setItem(STATUS_STORAGE_KEY, search.status)
      }
    }
  }, [search.status])

  const handleStatusChange = useCallback(
    (newStatus: string) => {
      setStatus(newStatus)
      if (typeof window !== "undefined") {
        localStorage.setItem(STATUS_STORAGE_KEY, newStatus)
      }
      navigate({
        to: "/WMS/production-orders/production-orders-list",
        search: {
          page: 1,
          page_size: pageSize,
          status: newStatus || undefined,
        },
        replace: true,
      })
    },
    [navigate, pageSize],
  )

  const canPrint = selected.length > 0
  const { data: ordersResponse } = useQuery<ProductionOrderListResponseDTO>({
    queryKey: ["production-orders", "stats"],
    queryFn: () =>
      ProductionOrderService.getAllProductionOrders(
        undefined,
        undefined,
        undefined,
        undefined,
        1,
        1000,
      ),
  })
  const orders = ordersResponse?.items ?? []

  const stats = useMemo(() => {
    const active = orders.filter((o) => o.status === "active").length
    const closed = orders.filter((o) => o.status === "closed").length
    const draft = orders.filter((o) => o.status === "draft").length
    return {
      total: orders.length,
      active,
      closed,
      draft,
      selected: selected.length,
    }
  }, [orders, selected.length])

  const selectedRegisterPayload = useMemo(
    () => ({
      documentType: "PRODUCTION_ORDER",
      title: "Lista zaznaczonych zleceń produkcyjnych",
      periodLabel: status || "all",
      rows: selected.map((order) => ({
        number: order.number ?? "—",
        status: getProductionOrderStatusLabel(order.status),
        warehouse: "—",
        partnerName: order.output_product_id ?? "—",
        owner: order.technology_id ?? "—",
        createdAt:
          typeof order.created_at === "string" && order.created_at
            ? new Date(order.created_at).toLocaleString()
            : "—",
      })),
    }),
    [selected, status],
  )

  const previewSelectedList = useCallback(async () => {
    if (!canPrint) return
    await previewPostedDocumentPdf(
      "/v1/document-registers/pdf",
      selectedRegisterPayload,
      "production-orders-register.pdf",
      "pl",
    )
  }, [canPrint, selectedRegisterPayload])

  const downloadSelectedList = useCallback(async () => {
    if (!canPrint) return
    await previewPostedDocumentPdf(
      "/v1/document-registers/pdf",
      selectedRegisterPayload,
      "production-orders-register.pdf",
      "en",
    )
  }, [canPrint, selectedRegisterPayload])

  const handleSelectionChange = useCallback(
    (items: ProductionOrderListDTO[]) => {
      setSelected(items)
    },
    [],
  )

  const exportCsv = useCallback(() => {
    if (!selected.length) return
    const rows = [
      [
        "Number",
        "Status",
        "TargetQty",
        "ProducedQty",
        "BatchSize",
        "CreatedAt",
      ].join(","),
      ...selected.map((o) =>
        [
          o.number,
          o.status,
          o.target_qty,
          o.produced_qty,
          o.batch_size,
          o.created_at,
        ]
          .map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`)
          .join(","),
      ),
    ]
    const csv = rows.join("\n")
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" })
    const url = URL.createObjectURL(blob)
    const a = document.createElement("a")
    a.href = url
    a.download = `production-orders-${new Date()
      .toISOString()
      .slice(0, 10)}.csv`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
    toast({ title: "CSV wygenerowany", status: "success" })
  }, [selected, toast])

  return (
    <Container
      maxW="9xl"
      py={{ base: 4, lg: 3 }}
      h={{ base: "auto", lg: "full" }}
      minH={{ base: "auto", lg: 0 }}
      display={{ base: "block", lg: "flex" }}
      flexDirection={{ base: "column", lg: "column" }}
      overflow={{ base: "visible", lg: "hidden" }}
    >
      <VStack
        spacing={4}
        align="stretch"
        h={{ base: "auto", lg: "full" }}
        minH={{ base: "auto", lg: 0 }}
        flex="1"
      >
        <Box
          bgGradient="linear(to-r, teal.600, blue.600)"
          color="white"
          borderRadius="2xl"
          px={{ base: 5, md: 8 }}
          py={{ base: 4, md: 5 }}
          boxShadow="lg"
        >
          <Flex
            direction={{ base: "column", md: "row" }}
            justify="space-between"
            align={{ base: "flex-start", md: "center" }}
            gap={4}
          >
            <Box>
              <Heading size="lg">Zlecenia produkcyjne</Heading>
              <Text mt={1} opacity={0.95}>
                Operacyjne zarządzanie zleceniami i monitoring realizacji.
              </Text>
            </Box>
            <Badge
              bg="whiteAlpha.250"
              color="white"
              borderRadius="full"
              px={3}
              py={1}
            >
              Zaznaczone: {stats.selected}
            </Badge>
          </Flex>
        </Box>

        <HStack spacing={3} align="stretch">
          <Stat
            size="sm"
            bg={statBg}
            borderWidth="1px"
            borderColor={statBorder}
            borderRadius="xl"
            px={3}
            py={1.5}
          >
            <StatLabel fontSize="xs" color={statLabelColor}>
              Razem
            </StatLabel>
            <StatNumber fontSize="lg" color={statValueColor}>
              {stats.total}
            </StatNumber>
          </Stat>
          <Stat
            size="sm"
            bg={statBg}
            borderWidth="1px"
            borderColor={statBorder}
            borderRadius="xl"
            px={3}
            py={1.5}
          >
            <StatLabel fontSize="xs" color={statLabelColor}>
              Aktywne
            </StatLabel>
            <StatNumber fontSize="lg" color={statValueColor}>
              {stats.active}
            </StatNumber>
          </Stat>
          <Stat
            size="sm"
            bg={statBg}
            borderWidth="1px"
            borderColor={statBorder}
            borderRadius="xl"
            px={3}
            py={1.5}
          >
            <StatLabel fontSize="xs" color={statLabelColor}>
              Zamknięte
            </StatLabel>
            <StatNumber fontSize="lg" color={statValueColor}>
              {stats.closed}
            </StatNumber>
          </Stat>
          <Stat
            size="sm"
            bg={statBg}
            borderWidth="1px"
            borderColor={statBorder}
            borderRadius="xl"
            px={3}
            py={1.5}
          >
            <StatLabel fontSize="xs" color={statLabelColor}>
              Szkic
            </StatLabel>
            <StatNumber fontSize="lg" color={statValueColor}>
              {stats.draft}
            </StatNumber>
          </Stat>
        </HStack>

        <Box
          bg={panelBg}
          borderWidth="1px"
          borderColor={panelBorder}
          borderRadius="2xl"
          p={{ base: 3, md: 4 }}
          boxShadow="sm"
          display="flex"
          flexDirection="column"
          flex="1"
          minH={0}
          overflow="hidden"
        >
          <Flex
            direction={{ base: "column", lg: "row" }}
            align={{ base: "stretch", lg: "center" }}
            justify="space-between"
            gap={3}
            mb={4}
          >
            <HStack flex={1}>
              <IconButton
                size="sm"
                aria-label="Szukaj"
                icon={<FiSearch />}
                isDisabled
              />
              <Input
                size="sm"
                placeholder="Szukaj numeru, kodu lub nazwy technologii"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                borderRadius="xl"
              />
              <Select
                size="sm"
                placeholder="Status"
                value={status}
                onChange={(e) => handleStatusChange(e.target.value)}
                maxW="220px"
                borderRadius="xl"
              >
                {PRODUCTION_ORDER_STATUS_OPTIONS.map((option) => (
                  <option key={option.value || "all"} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Select>
            </HStack>
            <HStack spacing={2} justify={{ base: "flex-end", lg: "initial" }}>
              <Select
                size="sm"
                maxW={{ base: "100%", lg: "170px" }}
                value={String(pageSize)}
                onChange={(event) =>
                  navigate({
                    to: "/WMS/production-orders/production-orders-list",
                    search: {
                      page: 1,
                      page_size: Number(event.target.value),
                      status: status || undefined,
                    },
                  })
                }
                borderRadius="xl"
              >
                {PAGE_SIZE_OPTIONS.map((size) => (
                  <option key={size} value={size}>
                    {size} / strona
                  </option>
                ))}
              </Select>
              <PrintLanguageSelect
                onPrintPL={previewSelectedList}
                onPrintEN={downloadSelectedList}
                isDisabledPL={!canPrint}
                isDisabledEN={!canPrint}
              />
              <IconButton
                size="sm"
                aria-label="Eksport CSV zaznaczonych"
                icon={<FiDownload />}
                onClick={exportCsv}
                isDisabled={!canPrint}
                borderRadius="xl"
              />
              <Tooltip label="Dodaj zlecenie" hasArrow placement="top">
                <IconButton
                  size="sm"
                  colorScheme="green"
                  aria-label="Dodaj zlecenie"
                  icon={<FiPlus />}
                  onClick={() =>
                    navigate({ to: "/WMS/production-orders/create" })
                  }
                  borderRadius="xl"
                />
              </Tooltip>
            </HStack>
          </Flex>

          <ProductionOrderList
            page={page}
            pageSize={pageSize}
            searchQuery={query}
            status={status}
            onChangePage={(nextPage) =>
              navigate({
                to: "/WMS/production-orders/production-orders-list",
                search: {
                  page: nextPage,
                  page_size: pageSize,
                  status: status || undefined,
                },
              })
            }
            onSelectionChange={handleSelectionChange}
          />
        </Box>
      </VStack>
    </Container>
  )
}
